// Subtle premium notification chime via Web Audio (no asset needed).
let audioCtx;

function getCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  audioCtx = audioCtx || new AC();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

// Browsers block Web Audio until a user gesture. Unlock once on first interaction
// so chimes/alerts can play later (including from background timers).
export function setupAudioUnlock() {
  if (typeof window === "undefined") return;
  const handler = () => { try { getCtx(); } catch (e) {} };
  ["pointerdown", "keydown", "touchstart", "click"].forEach((ev) =>
    window.addEventListener(ev, handler, { once: true, passive: true })
  );
}

export function canNotify() {
  return typeof window !== "undefined" && "Notification" in window;
}

export async function requestNotifyPermission() {
  if (!canNotify()) return "unsupported";
  try { return await Notification.requestPermission(); }
  catch { return "denied"; }
}

export function notifPermission() {
  return canNotify() ? Notification.permission : "unsupported";
}

export function showNotification(title, body, onClick) {
  if (!canNotify() || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      tag: "imperium-chat",
      renotify: true,
    });
    n.onclick = () => { try { window.focus(); } catch (e) {} n.close(); if (onClick) onClick(); };
  } catch (e) { /* silent */ }
}

export function playSuccessChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    audioCtx = audioCtx || new AC();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const now = audioCtx.currentTime;
    // Joyful major arpeggio C5 - E5 - G5 - C6, soft bell-like sine timbre.
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      // light harmonic for a shimmering bell feel
      const osc2 = audioCtx.createOscillator();
      osc2.type = "triangle";
      osc2.frequency.value = freq * 2;
      const gain2 = audioCtx.createGain();
      osc.connect(gain); gain.connect(audioCtx.destination);
      osc2.connect(gain2); gain2.connect(audioCtx.destination);
      const t = now + i * 0.11;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.2, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.55);
      gain2.gain.setValueAtTime(0.0001, t);
      gain2.gain.exponentialRampToValueAtTime(0.05, t + 0.02);
      gain2.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
      osc.start(t); osc.stop(t + 0.6);
      osc2.start(t); osc2.stop(t + 0.45);
    });
  } catch (e) { /* autoplay/policy — silent */ }
}

// One warm bell strike: sine fundamental + soft triangle harmonic, long gentle decay.
function _bell(ctx, freq, t, peak = 0.22, dur = 0.7) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  const h = ctx.createOscillator();
  const hg = ctx.createGain();
  h.type = "triangle";
  h.frequency.value = freq * 2.01; // slight detune → shimmer
  osc.connect(gain); gain.connect(ctx.destination);
  h.connect(hg); hg.connect(ctx.destination);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(peak, t + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  hg.gain.setValueAtTime(0.0001, t);
  hg.gain.exponentialRampToValueAtTime(peak * 0.28, t + 0.012);
  hg.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.7);
  osc.start(t); osc.stop(t + dur + 0.05);
  h.start(t); h.stop(t + dur * 0.7 + 0.05);
}

// New client request (admin): a warm "concierge bell" motif played twice —
// pleasant and elegant, yet bright and repeated so it's never missed.
export function playNewOrderChime() {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    audioCtx = ctx;
    const now = audioCtx.currentTime;
    // E6 → B5, then the same pair again after a short gap.
    _bell(audioCtx, 1318.51, now + 0.00, 0.24, 0.75);      // E6
    _bell(audioCtx, 987.77,  now + 0.17, 0.22, 0.85);      // B5
    _bell(audioCtx, 1318.51, now + 0.58, 0.24, 0.75);      // E6 (repeat)
    _bell(audioCtx, 987.77,  now + 0.75, 0.22, 0.95);      // B5 (repeat)
  } catch (e) { /* autoplay/policy — silent */ }
}


export function playChime() {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    audioCtx = ctx;
    const now = audioCtx.currentTime;
    // Two-tone ascending — clean, discreet, premium.
    const notes = [783.99, 1174.66]; // G5 -> D6
    notes.forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      const t = now + i * 0.12;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.16, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      osc.start(t);
      osc.stop(t + 0.32);
    });
  } catch (e) { /* autoplay/policy — silent */ }
}

// Distinct EXPRESS/priority-ticket alarm — a sweeping two-tone "siren whoop",
// intentionally different (sawtooth glide) from the order alert and the chime.
export function playExpressAlert() {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    audioCtx = ctx;
    const now = audioCtx.currentTime;
    // Two rising "whoop" sweeps with a bright sawtooth — unmistakable & urgent.
    for (let i = 0; i < 2; i++) {
      const t = now + i * 0.42;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(660, t);
      osc.frequency.exponentialRampToValueAtTime(1320, t + 0.3);
      osc.frequency.exponentialRampToValueAtTime(880, t + 0.4);
      const filter = audioCtx.createBiquadFilter();
      filter.type = "bandpass";
      filter.frequency.value = 1400;
      filter.Q.value = 0.9;
      osc.connect(filter); filter.connect(gain); gain.connect(audioCtx.destination);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.26, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4);
      osc.start(t); osc.stop(t + 0.42);
    }
  } catch (e) { /* autoplay/policy — silent */ }
}

export function playAlert() {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    audioCtx = ctx;
    const now = audioCtx.currentTime;
    // Insistent descending three-note alarm, repeated tightly.
    const notes = [1318.5, 1046.5, 1318.5]; // E6 - C6 - E6
    notes.forEach((freq, i) => {
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      const filter = audioCtx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 2600;
      osc.connect(filter); filter.connect(gain); gain.connect(audioCtx.destination);
      const t = now + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.22, t + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      osc.start(t); osc.stop(t + 0.18);
    });
  } catch (e) { /* autoplay/policy — silent */ }
}


/* ============================================================================
   Customizable notification sounds — PER-EVENT preferences, synced to the
   server (so choices follow the user across devices), with a small library of
   pleasant built-in chimes + custom uploaded audio (MP3/etc.).
   ============================================================================ */
import { api } from "@/lib/api";

const PREFS_KEY = "imp_notif_prefs";
export const NOTIF_EVENT_KEYS = ["order", "message", "ready"];
const DEFAULT_BY_KIND = { order: "concierge", ready: "arpege", message: "carillon" };

const _defaultEvents = () =>
  NOTIF_EVENT_KEYS.reduce((acc, k) => ((acc[k] = { sound: "auto", customUrl: null, customName: "" }), acc), {});
const DEFAULT_PREFS = { enabled: true, volume: 0.8, events: _defaultEvents() };

// Accept both the new per-event shape and the legacy single-sound shape.
function _normalize(raw) {
  const p = { ...DEFAULT_PREFS, ...(raw || {}) };
  p.enabled = raw?.enabled !== false;
  p.volume = typeof raw?.volume === "number" ? raw.volume : 0.8;
  const events = _defaultEvents();
  if (raw?.events && typeof raw.events === "object") {
    NOTIF_EVENT_KEYS.forEach((k) => {
      const e = raw.events[k] || {};
      events[k] = {
        sound: e.sound || "auto",
        customUrl: e.customUrl || null,
        customName: e.customName || "",
      };
    });
  } else if (raw?.sound) {
    // Legacy migration: apply the single old sound to every event.
    NOTIF_EVENT_KEYS.forEach((k) => {
      events[k] = { sound: raw.sound, customUrl: raw.customUrl || null, customName: raw.customName || "" };
    });
  }
  p.events = events;
  return p;
}

export function getNotifPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return _normalize(raw ? JSON.parse(raw) : null);
  } catch (e) {
    return _normalize(null);
  }
}

function _persistLocal(prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch (e) {}
}

let _saveTimer = null;
export function saveNotifPrefsToServer(prefs) {
  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => {
    api.put("/notification-prefs", prefs).catch(() => {});
  }, 400);
}

// Pull the server copy (source of truth) into localStorage — call on app/login load.
export async function loadNotifPrefsFromServer() {
  try {
    const { data } = await api.get("/notification-prefs");
    const norm = _normalize(data);
    _persistLocal(norm);
    return norm;
  } catch (e) {
    return getNotifPrefs();
  }
}

// Patch top-level (enabled/volume). Persists locally + syncs to server.
export function setNotifPrefs(patch) {
  const next = _normalize({ ...getNotifPrefs(), ...patch });
  _persistLocal(next);
  saveNotifPrefsToServer(next);
  return next;
}

// Patch a single event's sound choice. Persists locally + syncs to server.
export function setEventPref(kind, patch) {
  const cur = getNotifPrefs();
  const next = _normalize({ ...cur, events: { ...cur.events, [kind]: { ...cur.events[kind], ...patch } } });
  _persistLocal(next);
  saveNotifPrefsToServer(next);
  return next;
}

// A single tone helper with volume scaling.
function _tone(ctx, { type = "sine", freq, t, peak, dur, harmonic = 0 }, v) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.connect(gain); gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak * v), t + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.start(t); osc.stop(t + dur + 0.05);
  if (harmonic) {
    const h = ctx.createOscillator(); const hg = ctx.createGain();
    h.type = "triangle"; h.frequency.value = freq * 2.01;
    h.connect(hg); hg.connect(ctx.destination);
    hg.gain.setValueAtTime(0.0001, t);
    hg.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak * harmonic * v), t + 0.012);
    hg.gain.exponentialRampToValueAtTime(0.0001, t + dur * 0.7);
    h.start(t); h.stop(t + dur * 0.7 + 0.05);
  }
}

// Frequency-glide voice — great for comic boings and futuristic sweeps.
function _sweep(ctx, { type = "sine", from, to, t, dur, peak = 0.2, curve = "exp" }, v) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, t);
  if (curve === "lin") osc.frequency.linearRampToValueAtTime(to, t + dur);
  else osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), t + dur);
  osc.connect(gain); gain.connect(ctx.destination);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak * v), t + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.start(t); osc.stop(t + dur + 0.05);
}

// Built-in sound library. Each play(ctx, v) renders the chime at master volume v.
export const NOTIF_SOUNDS = {
  concierge: {
    label: "Cloche concierge",
    play: (ctx, v) => { const n = ctx.currentTime;
      _bell(ctx, 1318.51, n, 0.24 * v, 0.75); _bell(ctx, 987.77, n + 0.17, 0.22 * v, 0.85);
      _bell(ctx, 1318.51, n + 0.58, 0.24 * v, 0.75); _bell(ctx, 987.77, n + 0.75, 0.22 * v, 0.95); },
  },
  arpege: {
    label: "Arpège joyeux",
    play: (ctx, v) => { const n = ctx.currentTime; [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
      _tone(ctx, { freq: f, t: n + i * 0.11, peak: 0.2, dur: 0.55, harmonic: 0.25 }, v)); },
  },
  carillon: {
    label: "Carillon doux",
    play: (ctx, v) => { const n = ctx.currentTime; [783.99, 1174.66].forEach((f, i) =>
      _tone(ctx, { freq: f, t: n + i * 0.12, peak: 0.18, dur: 0.32 }, v)); },
  },
  marimba: {
    label: "Marimba",
    play: (ctx, v) => { const n = ctx.currentTime; [659.25, 987.77, 1318.51].forEach((f, i) =>
      _tone(ctx, { type: "triangle", freq: f, t: n + i * 0.09, peak: 0.24, dur: 0.28, harmonic: 0.15 }, v)); },
  },
  cristal: {
    label: "Ping cristal",
    play: (ctx, v) => { const n = ctx.currentTime;
      _tone(ctx, { freq: 1760, t: n, peak: 0.2, dur: 0.5, harmonic: 0.4 }, v);
      _tone(ctx, { freq: 2637, t: n + 0.02, peak: 0.1, dur: 0.35 }, v); },
  },
  pulse: {
    label: "Pulsation moderne",
    play: (ctx, v) => { const n = ctx.currentTime; [0, 0.16, 0.32].forEach((dt, i) =>
      _tone(ctx, { type: "sine", freq: 880, t: n + dt, peak: 0.2 - i * 0.03, dur: 0.14 }, v)); },
  },

  // ---------------- Comiques ----------------
  boing: {
    label: "😜 Boing ressort",
    play: (ctx, v) => { const n = ctx.currentTime;
      _sweep(ctx, { type: "sine", from: 180, to: 720, t: n, dur: 0.16, peak: 0.26 }, v);
      _sweep(ctx, { type: "sine", from: 720, to: 220, t: n + 0.16, dur: 0.34, peak: 0.24 }, v); },
  },
  coin: {
    label: "🪙 Pièce rétro",
    play: (ctx, v) => { const n = ctx.currentTime;
      _tone(ctx, { type: "square", freq: 987.77, t: n, peak: 0.16, dur: 0.09 }, v);
      _tone(ctx, { type: "square", freq: 1318.51, t: n + 0.09, peak: 0.18, dur: 0.5 }, v); },
  },
  bubble: {
    label: "🫧 Bulles pop",
    play: (ctx, v) => { const n = ctx.currentTime; [440, 620, 880, 1240].forEach((f, i) =>
      _sweep(ctx, { type: "sine", from: f, to: f * 1.5, t: n + i * 0.07, dur: 0.09, peak: 0.22 }, v)); },
  },
  honk: {
    label: "📯 Klaxon cartoon",
    play: (ctx, v) => { const n = ctx.currentTime;
      _tone(ctx, { type: "sawtooth", freq: 233.08, t: n, peak: 0.22, dur: 0.18, harmonic: 0.3 }, v);
      _tone(ctx, { type: "sawtooth", freq: 174.61, t: n + 0.22, peak: 0.22, dur: 0.26, harmonic: 0.3 }, v); },
  },
  wobble: {
    label: "🤪 Slide rigolo",
    play: (ctx, v) => { const n = ctx.currentTime;
      _sweep(ctx, { type: "triangle", from: 300, to: 900, t: n, dur: 0.22, peak: 0.24 }, v);
      _sweep(ctx, { type: "triangle", from: 900, to: 500, t: n + 0.2, dur: 0.14, peak: 0.2 }, v);
      _tone(ctx, { type: "triangle", freq: 700, t: n + 0.34, peak: 0.2, dur: 0.18, harmonic: 0.2 }, v); },
  },

  // ---------------- Futuristes ----------------
  laser: {
    label: "🔫 Laser sci-fi",
    play: (ctx, v) => { const n = ctx.currentTime;
      _sweep(ctx, { type: "sawtooth", from: 1600, to: 180, t: n, dur: 0.28, peak: 0.22 }, v);
      _tone(ctx, { type: "sine", freq: 160, t: n + 0.24, peak: 0.14, dur: 0.16 }, v); },
  },
  warp: {
    label: "🌀 Hyper-warp",
    play: (ctx, v) => { const n = ctx.currentTime;
      _sweep(ctx, { type: "sine", from: 220, to: 1760, t: n, dur: 0.5, peak: 0.22, curve: "exp" }, v);
      _sweep(ctx, { type: "triangle", from: 330, to: 2637, t: n + 0.02, dur: 0.5, peak: 0.1 }, v); },
  },
  hologram: {
    label: "🛸 Hologramme",
    play: (ctx, v) => { const n = ctx.currentTime;
      _tone(ctx, { type: "sine", freq: 880, t: n, peak: 0.18, dur: 0.7, harmonic: 0.5 }, v);
      _tone(ctx, { type: "sine", freq: 884, t: n, peak: 0.14, dur: 0.7 }, v);
      _tone(ctx, { type: "sine", freq: 1760, t: n + 0.18, peak: 0.1, dur: 0.4, harmonic: 0.3 }, v); },
  },
  droid: {
    label: "🤖 Droïde",
    play: (ctx, v) => { const n = ctx.currentTime; const notes = [740, 1180, 640, 1500, 980];
      notes.forEach((f, i) => _sweep(ctx, { type: "square", from: f, to: f * (i % 2 ? 1.4 : 0.7), t: n + i * 0.08, dur: 0.07, peak: 0.16 }, v)); },
  },
  quantum: {
    label: "⚛️ Impulsion quantique",
    play: (ctx, v) => { const n = ctx.currentTime;
      _tone(ctx, { type: "sine", freq: 1318.51, t: n, peak: 0.2, dur: 0.24, harmonic: 0.4 }, v);
      _sweep(ctx, { type: "sine", from: 1318.51, to: 2637, t: n + 0.1, dur: 0.3, peak: 0.12 }, v);
      _tone(ctx, { type: "triangle", freq: 3520, t: n + 0.24, peak: 0.08, dur: 0.3 }, v); },
  },
};

let _customEl = null;
function _playCustom(url, v) {
  try {
    _customEl = _customEl || new Audio();
    _customEl.src = url;
    _customEl.volume = Math.max(0, Math.min(1, v));
    _customEl.currentTime = 0;
    _customEl.play().catch(() => {});
  } catch (e) {}
}

function _playSoundId(id, v) {
  if (id === "custom") return; // handled by caller with a url
  const entry = NOTIF_SOUNDS[id] || NOTIF_SOUNDS.carillon;
  try { const ctx = getCtx(); if (ctx) { audioCtx = ctx; entry.play(ctx, v); } } catch (e) {}
}

// Resolve + play the sound for a given notification event, honoring per-event prefs.
export function playNotify(kind = "message") {
  const p = getNotifPrefs();
  if (!p.enabled) return;
  const v = typeof p.volume === "number" ? p.volume : 0.8;
  const ev = p.events?.[kind] || {};
  if (ev.sound === "custom" && ev.customUrl) { _playCustom(ev.customUrl, v); return; }
  const id = ev.sound && ev.sound !== "auto" ? ev.sound : (DEFAULT_BY_KIND[kind] || "carillon");
  _playSoundId(id, v);
}

// Preview a specific sound (used in the settings panel).
export function previewNotify({ sound, volume = 0.8, customUrl = null }) {
  if (sound === "custom" && customUrl) { _playCustom(customUrl, volume); return; }
  const id = sound && sound !== "auto" ? sound : DEFAULT_BY_KIND.order;
  _playSoundId(id, volume);
}
